import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import type { AccountLoginUser } from '@automatick/shared-client-ng';
import { AuthService, type Session, type Token } from '@seech/auth-ng';
import { RealTimeCommunicationService } from '@seech/core-ng';
import { NotificationHubContract } from '@automatick/shared-ts';
import {
  ChannelClientService,
  type ChannelBindingResponse,
  type ChatOutboundMessageResponse,
  ChatSessionMessageResponse,
  ChatSessionResponse,
  ConversationClientService,
  ProjectsClientService,
} from '@automatick/shared-ng';
import { EMPTY, of, Subscription, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import {
  AppStateService,
  type WorkspaceBootstrapState,
} from './app-state.service';
import { buildIdleRealtimeConnectionHealth } from '../shared/realtime/realtime-connection-health';
import { CurrentDeviceContextService } from './current-device-context.service';
import { WebChatMessageRuntime } from './web-chat-message.runtime';
import { WebChatRealtimeRuntime } from './web-chat-realtime.runtime';
import { WebChatProjectRuntime } from './web-chat-project.runtime';
import { WebChatSessionCatalogRuntime } from './web-chat-session-catalog.runtime';
import {
  isExactWebChatSessionScope,
  WebChatSessionRuntime,
  type WebChatSessionHistoryResult,
  WebChatWorkspaceUnavailableError,
} from './web-chat-session.runtime';
import { WebChatStateStore } from './web-chat-state.store';
import { WebChatThoughtTraceRuntime } from './web-chat-thought-trace.runtime';
import { WebChatTranscriptRuntime } from './web-chat-transcript.runtime';
import {
  PendingOutboundMessage,
  RespondToInteractionOptions,
  SendWebChatMessageOptions,
  TRANSCRIPT_REFRESH_DELAY_MS,
  isCanonicalWebChatIdentifier,
  WEBCHAT_PLATFORM_CODE,
  WEB_CHAT_TRANSCRIPT_REFRESH_MAX_RETRIES,
  WebChatThoughtTraceSummary,
} from './web-chat.models';

export { normalizeWebChatPresentations } from './web-chat-contract-normalizers';
export type {
  PendingOutboundMessage,
  RespondToInteractionOptions,
  SendWebChatMessageOptions,
  WebChatRenderEvent,
  WebChatState,
  WebChatThoughtTraceSummary,
} from './web-chat.models';

interface SendSessionResult {
  readonly response: ChatOutboundMessageResponse | null;
  readonly userMessage: ChatSessionMessageResponse;
  readonly idempotencyKey: string;
  readonly failureReason: string | null;
}

type OwnedWebChatBinding = ChannelBindingResponse & { readonly id: string };

interface WorkspaceWebChatBootstrapResult {
  readonly binding: OwnedWebChatBinding;
  readonly session: ChatSessionResponse | null;
  readonly history: WebChatSessionHistoryResult | null;
}

const pendingOutboundLeaseOwner = Symbol('pending-outbound-lease-owner');

export interface PendingOutboundLease {
  readonly [pendingOutboundLeaseOwner]: symbol;
}

@Injectable({ providedIn: 'root' })
export class WebChatService implements OnDestroy {
  private readonly channelClient = inject(ChannelClientService);
  private readonly conversationClient = inject(ConversationClientService);
  private readonly projectsClient = inject(ProjectsClientService);
  private readonly authService = inject(
    AuthService<AccountLoginUser, Session, Token>,
  );
  private readonly appState = inject(AppStateService);
  private readonly currentDeviceContext = inject(CurrentDeviceContextService);
  private readonly realtime = inject(RealTimeCommunicationService);
  private readonly zone = inject(NgZone);
  private readonly stateStore = new WebChatStateStore(
    this.channelClient,
    this.conversationClient,
    this.projectsClient,
    this.authService,
    this.appState,
    this.currentDeviceContext,
    this.realtime,
  );
  private readonly messageRuntime = new WebChatMessageRuntime(this.stateStore);
  private readonly thoughtTraceRuntime = new WebChatThoughtTraceRuntime(
    this.stateStore,
    this.messageRuntime,
  );
  private readonly transcriptRuntime = new WebChatTranscriptRuntime(
    this.stateStore,
    this.messageRuntime,
  );
  private readonly realtimeRuntime = new WebChatRealtimeRuntime(
    this.stateStore,
    this.messageRuntime,
    this.thoughtTraceRuntime,
    this.transcriptRuntime,
  );
  private readonly sessionRuntime = new WebChatSessionRuntime(
    this.stateStore,
    this.messageRuntime,
    this.thoughtTraceRuntime,
    this.realtimeRuntime,
  );
  private readonly sessionCatalogRuntime = new WebChatSessionCatalogRuntime(
    this.stateStore,
    this.sessionRuntime,
    this.messageRuntime,
    this.realtimeRuntime,
  );
  private readonly projectRuntime = new WebChatProjectRuntime(
    this.stateStore,
    this.sessionCatalogRuntime,
  );
  private bootstrapGeneration = 0;
  private workspaceAuthoritySubscription: Subscription | null = null;
  private bootstrapRequestSubscription: Subscription | null = null;
  private acceptedWorkspaceId: string | null = null;
  private requestedProjectWorkspaceId: string | null = null;
  private projectWorkspaceRequestInvalidated = false;
  private workspaceReconciliationActivated = false;
  private activePendingOutboundLease: PendingOutboundLease | null = null;
  private destroyed = false;
  readonly state$ = this.stateStore.state$;

  constructor() {
    this.realtime.events$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.zone.run(() => this.realtimeRuntime.handleRealtimeEnvelope(event));
    });

    this.realtime.status$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      this.zone.run(() => this.realtimeRuntime.applyRealtimeStatus(status));
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.bootstrapGeneration += 1;
    this.releaseSubscription(this.workspaceAuthoritySubscription);
    this.workspaceAuthoritySubscription = null;
    this.releaseBootstrapRequest();
    this.activePendingOutboundLease = null;
    this.pendingOutbound = null;
    this.projectRuntime.destroy();
    this.sessionCatalogRuntime.destroy();
    this.sessionRuntime.destroy();
    this.destroy$.next();
    this.destroy$.complete();
    this.stateStore.clearAwaitingResponseTimeout();
    this.disconnectWorkspaceRealtime();
    this.transcriptRuntime.destroy();
  }

  bootstrap(): void {
    if (this.workspaceReconciliationActivated || this.destroyed) return;
    this.workspaceReconciliationActivated = true;

    try {
      const subscription = this.appState.state$
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (state) => this.reconcileWorkspaceAuthority(state),
          error: () => this.reconcileWorkspaceAuthority(null),
          complete: () => this.reconcileWorkspaceAuthority(null),
        });
      this.workspaceAuthoritySubscription = subscription;
    } catch {
      this.reconcileWorkspaceAuthority(null);
    }
  }

  loadProjectsForWorkspace(workspaceId: string | null | undefined): void {
    const exactWorkspaceId = isCanonicalWebChatIdentifier(workspaceId)
      ? workspaceId
      : null;
    this.requestedProjectWorkspaceId = exactWorkspaceId;
    this.projectWorkspaceRequestInvalidated = false;
    this.loadProjectsForWorkspaceSafely(exactWorkspaceId);
  }

  createProject(name: string): void {
    this.projectRuntime.createProject(name);
  }

  sendMessage(content: string, platformAgentId?: string): void {
    this.sendMessageWithOptions(content, { platformAgentId });
  }

  sendMessageWithOptions(
    content: string,
    options?: SendWebChatMessageOptions,
  ): void {
    const { isSending, isStoppingResponse } = this.stateSubject.value;
    const normalizedContent = content.trim();
    const normalizedUploads = (options?.uploads ?? []).filter((upload) =>
      Boolean(
        this.messageRuntime.readAssetString(upload.asset.storageModifiedName),
      ),
    );
    if (
      isSending ||
      isStoppingResponse ||
      (!normalizedContent && normalizedUploads.length === 0)
    ) {
      return;
    }

    this.stateStore.beginAwaitingResponse();
    this.patch({ isSending: true });
    const cancel$ = this.stateStore.createActiveRequestCancel$();

    this.sessionRuntime
      .ensureSessionForOutbound(
        normalizedContent,
        normalizedUploads.length,
        options?.platformAgentId ?? null,
      )
      .pipe(
        switchMap((session) => {
          if (!session?.sessionId) {
            return of(null);
          }

          const idempotencyKey = crypto.randomUUID();
          const entryId = `local-${Date.now()}`;
          const userMessage: ChatSessionMessageResponse = {
            entryId,
            sessionId: session.sessionId,
            direction: 'Outbound',
            occurredOn: new Date().toISOString(),
            messageType: 'Text',
            content:
              normalizedContent ||
              `Sent ${normalizedUploads.length} attachment(s).`,
            renderKind: 'Final',
            status: 'Sending',
            idempotencyKey,
            attachments: this.messageRuntime.buildOptimisticAttachments(
              session.sessionId,
              entryId,
              normalizedUploads,
            ),
          };

          this.patch({
            messages: [...this.stateSubject.value.messages, userMessage],
          });

          return this.chatClient
            .sendSessionMessage({
              sessionId: session.sessionId,
              body: {
                messageType: 'Text',
                content: normalizedContent || undefined,
                requestedPlatformAgentId: options?.platformAgentId ?? undefined,
                requestedRuntimeMode:
                  options?.requestedRuntimeMode ?? undefined,
                streamResponseMode: this.messageRuntime.resolveStreamMode(),
                includeThoughtProcess:
                  this.messageRuntime.shouldIncludeThoughtProcess(),
                uploads:
                  normalizedUploads.length > 0 ? normalizedUploads : undefined,
                workRequestTemplate: options?.workRequestTemplate ?? undefined,
                idempotencyKey,
              },
            })
            .pipe(
              map((response): SendSessionResult => ({
                response,
                userMessage,
                idempotencyKey,
                failureReason: null,
              })),
              catchError((error) =>
                of<SendSessionResult>({
                  response: null,
                  userMessage,
                  idempotencyKey,
                  failureReason: this.stateStore.resolveRequestError(error),
                }),
              ),
            );
        }),
        finalize(() => {
          this.stateStore.completeActiveRequestCancel$(cancel$);
          this.patch({ isSending: false });
        }),
        takeUntil(cancel$),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (!result?.userMessage) {
          return;
        }

        const messages = this.stateSubject.value.messages.map((m) =>
          m.entryId === result.userMessage.entryId
            ? {
                ...m,
                occurredOn: result.response?.sentOn ?? m.occurredOn,
                status: result.response ? 'Delivered' : 'Failed',
                idempotencyKey:
                  result.response?.idempotencyKey ?? result.idempotencyKey,
                modelId:
                  result.response?.effectivePlatformAgentId ??
                  result.response?.requestedPlatformAgentId,
                content: result.response?.content ?? m.content,
                attachments: m.attachments,
                failureReason: result.response
                  ? null
                  : (result.failureReason ?? 'Failed to send message.'),
              }
            : m,
        );

        this.patch({ messages });

        if (result.response) {
          this.realtimeRuntime.scheduleTranscriptRefresh(
            1500,
            WEB_CHAT_TRANSCRIPT_REFRESH_MAX_RETRIES,
            true, // delta only — don't re-fetch entire history
          );
        }
      });
  }

  stopActiveResponse(): void {
    const state = this.stateSubject.value;
    if (state.isStoppingResponse) {
      return;
    }

    if (!state.isSending && !state.isAwaitingResponse) {
      return;
    }

    const sessionId = state.session?.sessionId?.trim();
    this.patch({ isStoppingResponse: true });
    this.stateStore.cancelActiveRequestTransport();

    if (!sessionId) {
      this.stateStore.resetAwaitingResponseState();
      return;
    }

    this.chatClient
      .cancelActiveSessionResponse({ sessionId })
      .pipe(
        catchError(() => {
          this.stateStore.resetAwaitingResponseState();
          return of(null);
        }),
        finalize(() => this.patch({ isStoppingResponse: false })),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.stateStore.resetAwaitingResponseState();
        this.realtimeRuntime.scheduleTranscriptRefresh(
          TRANSCRIPT_REFRESH_DELAY_MS,
          WEB_CHAT_TRANSCRIPT_REFRESH_MAX_RETRIES,
          true,
        );
      });
  }

  respondToInteraction(
    message: ChatSessionMessageResponse,
    options?: RespondToInteractionOptions,
  ): void {
    const { session, isSending, isStoppingResponse } = this.stateSubject.value;
    const instructionId = message.instructionId?.trim();
    const interactionType = message.interactionType?.trim();
    const interactionId = message.interactionId?.trim();
    if (
      !session?.sessionId ||
      isSending ||
      isStoppingResponse ||
      !instructionId ||
      !interactionType ||
      !interactionId
    ) {
      return;
    }

    const normalizedType = interactionType.toLowerCase();
    const payload: {
      approved?: boolean;
      selectedChoiceId?: string;
    } = {};

    if (normalizedType === 'approval') {
      payload.approved = options?.approved ?? true;
    } else if (normalizedType === 'choice') {
      payload.selectedChoiceId =
        options?.selectedChoiceId?.trim() ?? message.selectedChoiceId?.trim();
      if (!payload.selectedChoiceId) {
        return;
      }
    }

    this.stateStore.beginAwaitingResponse();
    this.patch({ isSending: true });
    const cancel$ = this.stateStore.createActiveRequestCancel$();
    this.chatClient
      .respondToSessionInteraction({
        sessionId: session.sessionId,
        body: {
          instructionId,
          interactionType,
          interactionId,
          approved: payload.approved,
          selectedChoiceId: payload.selectedChoiceId,
          comment: options?.comment?.trim() || undefined,
          requestedPlatformAgentId: options?.platformAgentId ?? undefined,
          streamResponseMode: this.messageRuntime.resolveStreamMode(),
          includeThoughtProcess:
            this.messageRuntime.shouldIncludeThoughtProcess(),
          idempotencyKey: crypto.randomUUID(),
        },
      })
      .pipe(
        finalize(() => {
          this.stateStore.completeActiveRequestCancel$(cancel$);
          this.patch({ isSending: false });
        }),
        takeUntil(cancel$),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          const messages = this.stateSubject.value.messages.map((candidate) =>
            candidate.entryId === message.entryId
              ? {
                  ...candidate,
                  selectedChoiceId:
                    payload.selectedChoiceId ?? candidate.selectedChoiceId,
                  status: 'Delivered',
                }
              : candidate,
          );
          this.patch({ messages });
          this.realtimeRuntime.scheduleTranscriptRefresh(
            1500,
            WEB_CHAT_TRANSCRIPT_REFRESH_MAX_RETRIES,
            true, // delta only
          );
        },
      });
  }

  searchSessions(search: string): void {
    this.sessionCatalogRuntime.searchSessions(search);
  }

  searchMessages(search: string): void {
    this.sessionCatalogRuntime.searchMessages(search);
  }

  loadThoughtTraceEntries(summary: WebChatThoughtTraceSummary): void {
    void this.thoughtTraceRuntime.loadThoughtTraceEntriesAsync(summary);
  }

  switchSession(sessionId: string): void {
    this.sessionCatalogRuntime.switchSession(sessionId);
  }

  createNewSession(): void {
    this.sessionCatalogRuntime.createNewSession();
  }

  renameSession(sessionId: string, title: string): void {
    this.sessionCatalogRuntime.renameSession(sessionId, title);
  }

  updateSessionPreferredPlatformAgent(
    sessionId: string,
    preferredPlatformAgentId: string | null,
  ): void {
    this.sessionCatalogRuntime.updateSessionPreferredPlatformAgent(
      sessionId,
      preferredPlatformAgentId,
    );
  }

  setProjectScope(projectId: string | null): void {
    this.sessionCatalogRuntime.setProjectScope(projectId);
  }

  archiveSession(sessionId: string): void {
    this.sessionCatalogRuntime.archiveSession(sessionId);
  }

  deleteSession(sessionId: string): void {
    this.sessionCatalogRuntime.deleteSession(sessionId);
  }

  retryBootstrap(): void {
    if (this.destroyed) return;
    if (!this.workspaceReconciliationActivated) {
      this.bootstrap();
      return;
    }

    const workspaceId = this.readSettledWorkspaceIdSafely();
    if (!workspaceId) {
      this.reconcileWorkspaceAuthority(null);
      return;
    }
    if (this.acceptedWorkspaceId !== workspaceId) {
      this.reconcileWorkspaceId(workspaceId);
      return;
    }

    this.invalidateBootstrapRequest();
    this.clearWorkspaceState('idle', '', workspaceId);
    this.startWorkspaceBootstrap(workspaceId);
  }

  activateSession(session: ChatSessionResponse): void {
    this.sessionCatalogRuntime.activateSession(session);
  }

  setPendingOutbound(pending: PendingOutboundMessage): void {
    this.stagePendingOutbound(pending);
  }

  stagePendingOutbound(pending: PendingOutboundMessage): PendingOutboundLease {
    const lease: PendingOutboundLease = Object.freeze({
      [pendingOutboundLeaseOwner]: Symbol('pending-outbound-owner'),
    });
    this.pendingOutbound = pending;
    this.activePendingOutboundLease = lease;
    return lease;
  }

  clearPendingOutbound(lease: PendingOutboundLease): boolean {
    if (this.activePendingOutboundLease !== lease) return false;
    this.activePendingOutboundLease = null;
    this.pendingOutbound = null;
    return true;
  }

  consumePendingOutbound(): PendingOutboundMessage | null {
    const pending = this.pendingOutbound;
    this.activePendingOutboundLease = null;
    this.pendingOutbound = null;
    return pending;
  }

  hasPendingOutbound(): boolean {
    return this.pendingOutbound !== null;
  }

  get stateSnapshot() {
    return this.stateStore.stateSnapshot;
  }

  private get stateSubject(): WebChatStateStore['stateSubject'] {
    return this.stateStore.stateSubject;
  }

  private get destroy$(): WebChatStateStore['destroy$'] {
    return this.stateStore.destroy$;
  }

  private get chatClient(): WebChatStateStore['chatClient'] {
    return this.stateStore.chatClient;
  }

  private get activeLiveSessionId(): string | null {
    return this.stateStore.activeLiveSessionId;
  }

  private set activeLiveSessionId(value: string | null) {
    this.stateStore.activeLiveSessionId = value;
  }

  private get activeLiveThreadId(): string | null {
    return this.stateStore.activeLiveThreadId;
  }

  private set activeLiveThreadId(value: string | null) {
    this.stateStore.activeLiveThreadId = value;
  }

  private get pendingOutbound(): PendingOutboundMessage | null {
    return this.stateStore.pendingOutbound;
  }

  private set pendingOutbound(value: PendingOutboundMessage | null) {
    this.stateStore.pendingOutbound = value;
  }

  private patch(partial: Parameters<WebChatStateStore['patch']>[0]): void {
    this.stateStore.patch(partial);
  }

  private reconcileWorkspaceAuthority(
    state: WorkspaceBootstrapState | null,
  ): void {
    if (this.destroyed) return;
    this.reconcileWorkspaceId(this.readSettledWorkspaceId(state));
  }

  private reconcileWorkspaceId(workspaceId: string | null): void {
    if (workspaceId === this.acceptedWorkspaceId) return;

    const previousWorkspaceId = this.acceptedWorkspaceId;
    this.invalidateBootstrapRequest();
    this.acceptedWorkspaceId = workspaceId;
    if (previousWorkspaceId !== null) {
      this.clearWorkspaceState('idle', '', workspaceId);
    }
    if (workspaceId) {
      this.restoreProjectWorkspaceRequest(workspaceId);
      this.startWorkspaceBootstrap(workspaceId);
    }
  }

  private startWorkspaceBootstrap(workspaceId: string): void {
    const generation = ++this.bootstrapGeneration;
    let settled = false;
    let subscription: Subscription | null = null;
    this.patch({ phase: 'bootstrapping', errorMessage: '' });

    try {
      const source = this.sessionRuntime.resolveBinding().pipe(
        take(1),
        switchMap((binding) => {
          if (!this.ownsBootstrap(generation, workspaceId)) return EMPTY;
          return binding
            ? of(binding)
            : this.sessionRuntime.createBinding().pipe(take(1));
        }),
        switchMap((binding) => {
          if (!this.ownsBootstrap(generation, workspaceId)) return EMPTY;
          if (!this.isOwnedWebChatBinding(binding, workspaceId)) {
            return throwError(() => new Error('invalid-binding'));
          }
          this.patch({ binding });
          return this.sessionRuntime.resolveSession(binding).pipe(
            take(1),
            map((session) => ({ binding, session })),
          );
        }),
        switchMap(({ binding, session }) => {
          if (!this.ownsBootstrap(generation, workspaceId)) return EMPTY;
          if (
            session !== null &&
            !isExactWebChatSessionScope(session, workspaceId, binding.id)
          ) {
            return throwError(() => new Error('invalid-session'));
          }
          this.patch({ session });
          if (!session) {
            return of<WorkspaceWebChatBootstrapResult>({
              binding,
              session: null,
              history: null,
            });
          }
          return this.sessionRuntime.loadCapabilitiesAndHistory(session).pipe(
            take(1),
            map((history): WorkspaceWebChatBootstrapResult => ({
              binding,
              session,
              history,
            })),
          );
        }),
        takeUntil(this.destroy$),
      );

      subscription = source.subscribe({
        next: (result) => {
          if (settled || !this.ownsBootstrap(generation, workspaceId)) return;
          settled = true;
          this.releaseBootstrapRequest();
          try {
            this.acceptWorkspaceBootstrap(generation, workspaceId, result);
          } catch {
            this.failWorkspaceBootstrap(generation, workspaceId);
          }
        },
        error: (error: unknown) => {
          if (settled || !this.ownsBootstrap(generation, workspaceId)) return;
          settled = true;
          this.releaseBootstrapRequest();
          this.failWorkspaceBootstrap(generation, workspaceId, error);
        },
        complete: () => {
          if (settled || !this.ownsBootstrap(generation, workspaceId)) return;
          settled = true;
          this.releaseBootstrapRequest();
          this.failWorkspaceBootstrap(generation, workspaceId);
        },
      });

      if (!settled && this.ownsBootstrap(generation, workspaceId)) {
        this.bootstrapRequestSubscription = subscription;
      } else {
        this.releaseSubscription(subscription);
      }
    } catch (error: unknown) {
      this.releaseSubscription(subscription);
      if (!settled && this.ownsBootstrap(generation, workspaceId)) {
        settled = true;
        this.failWorkspaceBootstrap(generation, workspaceId, error);
      }
    }
  }

  private acceptWorkspaceBootstrap(
    generation: number,
    workspaceId: string,
    result: WorkspaceWebChatBootstrapResult,
  ): void {
    if (!this.ownsBootstrap(generation, workspaceId)) return;
    if (
      !this.isOwnedWebChatBinding(result.binding, workspaceId) ||
      (result.session !== null &&
        !isExactWebChatSessionScope(
          result.session,
          workspaceId,
          result.binding.id,
        ))
    ) {
      this.failWorkspaceBootstrap(generation, workspaceId);
      return;
    }

    this.patch({
      phase: 'ready',
      binding: result.binding,
      session: result.session,
      uxCapabilities: result.history?.capabilities ?? null,
      messages: result.history?.messages ?? [],
      sessionFiles: result.history?.sessionFiles ?? [],
      thoughtTraces: result.history?.thoughtTraces ?? [],
      thoughtTraceEntriesById: {},
      loadingThoughtTraceIds: [],
      activeThoughtTraceId: null,
      finalRenderMessage: null,
      isThoughtChainActive: false,
      errorMessage: '',
    });

    if (result.session) this.realtimeRuntime.connectLive();
  }

  private failWorkspaceBootstrap(
    generation: number,
    workspaceId: string,
    error?: unknown,
  ): void {
    if (!this.ownsBootstrap(generation, workspaceId)) return;
    this.clearWorkspaceState(
      'error',
      error instanceof WebChatWorkspaceUnavailableError
        ? 'Chat is not available until a workspace is selected.'
        : 'Failed to initialize chat. Please try again.',
      workspaceId,
    );
  }

  private invalidateBootstrapRequest(): void {
    this.bootstrapGeneration += 1;
    this.releaseBootstrapRequest();
  }

  private releaseBootstrapRequest(): void {
    const subscription = this.bootstrapRequestSubscription;
    this.bootstrapRequestSubscription = null;
    this.releaseSubscription(subscription);
  }

  private releaseSubscription(subscription: Subscription | null): void {
    try {
      subscription?.unsubscribe();
    } catch {
      // Ownership is cleared before teardown so a hostile source stays inert.
    }
  }

  private clearWorkspaceState(
    phase: 'idle' | 'error' = 'idle',
    errorMessage = '',
    retainedProjectWorkspaceId: string | null = null,
  ): void {
    const retainedProjects = retainedProjectWorkspaceId
      ? this.stateSubject.value.projects.filter(
          (project) =>
            project.workspaceId === retainedProjectWorkspaceId &&
            isCanonicalWebChatIdentifier(project.projectId),
        )
      : [];
    const selectedProjectId = this.stateSubject.value.selectedProjectId;
    const retainedSelectedProjectId =
      selectedProjectId &&
      retainedProjects.some(
        (project) => project.projectId === selectedProjectId,
      )
        ? selectedProjectId
        : null;
    this.disconnectWorkspaceRealtime();
    if (!retainedProjectWorkspaceId) {
      this.invalidateProjectWorkspaceRequest();
    }
    this.stateStore.cancelActiveRequestTransport();
    this.stateStore.clearAwaitingResponseTimeout();
    this.activePendingOutboundLease = null;
    this.pendingOutbound = null;
    this.patch({
      phase,
      binding: null,
      session: null,
      sessions: [],
      projects: retainedProjects,
      selectedProjectId: retainedSelectedProjectId,
      uxCapabilities: null,
      messages: [],
      sessionFiles: [],
      isSending: false,
      isStoppingResponse: false,
      isAwaitingResponse: false,
      activeThoughtText: null,
      isLoadingHistory: false,
      isLoadingFiles: false,
      isSearchingSessions: false,
      isSearchingMessages: false,
      sessionSearchTerm: '',
      messageSearchTerm: '',
      liveTransport: 'idle',
      liveStatusMessage: '',
      realtimeHealth: buildIdleRealtimeConnectionHealth(
        NotificationHubContract.ReceiveMethod,
      ),
      latestRenderEvent: null,
      latestSearchRenderEvent: null,
      latestHealthCheckRenderEvent: null,
      recentRenderEvents: [],
      presentationRenderEvents: [],
      thoughtChain: [],
      thoughtTraces: [],
      thoughtTraceEntriesById: {},
      loadingThoughtTraceIds: [],
      activeThoughtTraceId: null,
      finalRenderMessage: null,
      isThoughtChainActive: false,
      errorMessage,
    });
  }

  private disconnectWorkspaceRealtime(): void {
    try {
      this.realtimeRuntime.resetLiveChannelState();
    } catch {
      this.stateStore.cancelActiveRequestTransport();
    }
    try {
      void this.realtime.disconnect().catch(() => undefined);
    } catch {
      // A synchronous transport failure cannot retain workspace authority.
    }
  }

  private invalidateProjectWorkspaceRequest(): void {
    this.projectWorkspaceRequestInvalidated =
      this.requestedProjectWorkspaceId !== null;
    this.loadProjectsForWorkspaceSafely(null);
  }

  private restoreProjectWorkspaceRequest(workspaceId: string): void {
    if (
      !this.projectWorkspaceRequestInvalidated ||
      this.requestedProjectWorkspaceId !== workspaceId
    ) {
      return;
    }
    this.projectWorkspaceRequestInvalidated = false;
    this.loadProjectsForWorkspaceSafely(workspaceId);
  }

  private loadProjectsForWorkspaceSafely(workspaceId: string | null): void {
    try {
      this.projectRuntime.loadProjectsForWorkspace(workspaceId);
    } catch {
      this.patch({ projects: [], selectedProjectId: null });
    }
  }

  private readSettledWorkspaceIdSafely(): string | null {
    try {
      return this.readSettledWorkspaceId(this.appState.getStateSnapshot());
    } catch {
      return null;
    }
  }

  private readSettledWorkspaceId(
    state: WorkspaceBootstrapState | null,
  ): string | null {
    if (
      !state ||
      state.isLoading ||
      !state.isLoaded ||
      state.errorMessage !== '' ||
      !isCanonicalWebChatIdentifier(state.activeWorkspaceId)
    ) {
      return null;
    }
    return state.activeWorkspaceId;
  }

  private isOwnedWebChatBinding(
    binding: ChannelBindingResponse | null,
    workspaceId: string,
  ): binding is OwnedWebChatBinding {
    return (
      binding !== null &&
      isCanonicalWebChatIdentifier(binding.id) &&
      binding.workspaceId === workspaceId &&
      binding.platformCode === WEBCHAT_PLATFORM_CODE &&
      binding.isEnabled !== false
    );
  }

  private ownsBootstrap(generation: number, workspaceId: string): boolean {
    return (
      !this.destroyed &&
      this.bootstrapGeneration === generation &&
      this.acceptedWorkspaceId === workspaceId
    );
  }
}
